import useDataObjectTransform from "./useDataObjectTransform"
import { SolrQueryBuilder } from "../services/solr/SolrQueryBuilder"
import { SolrApi } from "../services/solrApi"
import solrConfig from "../config/solr.json"

const queryConfig = solrConfig.query
const clientConfig = solrConfig.server
const solrClient = new SolrApi(clientConfig)

const useTripsData = () => {
  const { tripDataFilterAndTransform, tripOverrideTransform, experienceFacetsTransform } =
    useDataObjectTransform()

  const getOverrideTrips = (trips, expirationDate) => {
    const isExpired = expirationDate ? Date.parse(expirationDate) < Date.now() : false

    let override = []

    if (trips && trips.length > 0 && !isExpired) {
      override = tripOverrideTransform(trips)
    }

    return override
  }

  const getTripsData = async (nodeId, component = null, useOverrideTripsOnly = false) => {
    const overrideTrips = component
      ? getOverrideTrips(component?.trip_list_override, component?.expire_date)
      : []

    // If we have override trips and useOverrideTripsOnly is true, return only those trips
    if (overrideTrips.length > 0 && useOverrideTripsOnly) {
      return overrideTrips
    }

    const solrQuery = new SolrQueryBuilder(queryConfig)
    solrQuery
      // Query that have group field unset (null) or false (not true).
      .customq(["-bs_groups:true OR (*:* -bs_groups:[* TO *])"])
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        ...(nodeId && { its_primary_country_country_nid: nodeId }),
      })
      .sort({ its_popularity: "desc" })
      .rows(nodeId ? 9 : 10)

    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.response?.docs || null
    const transform = tripDataFilterAndTransform(solrRecords)
    return transform.slice(0, 9)
  }

  const getTripsByCountryAndDestination = async (
    destinationId, // kept for backwards compatibility, not used
    countryId = null,
    suggestedTrips = null,
    activeTripId = null
  ) => {
    let results = []
    const tripIds = new Set()
    const overrideTrips = suggestedTrips
      ? getOverrideTrips(suggestedTrips.trip_list_override, suggestedTrips.expire_date)
      : []

    // If we have override trips, return only those trips
    if (overrideTrips.length > 0) {
      return overrideTrips
    }

    if (countryId) {
      // Get country-based trips only (no destination fallback to avoid cross-country trips)
      const solrQuery = new SolrQueryBuilder(queryConfig)
      solrQuery
        .customq(["-bs_groups:true OR (*:* -bs_groups:[* TO *])"])
        .q({
          index_id: "trip_index",
          ss_type: "trip",
          its_primary_country_country_nid: countryId,
        })
        .sort({ bs_special_offer: "desc", its_popularity: "desc" })
        .rows(10)

      const countryRes = await solrClient.runQuery(solrQuery)
      const countrySolrRecords = countryRes?.response?.docs || []

      countrySolrRecords.forEach((record) => {
        results.push(record)
        tripIds.add(record.its_nid)
      })
    }

    if (activeTripId) {
      // filter results to exclude active trip
      results = results.filter((trip) => trip.its_nid[0] !== activeTripId)
    }
    const slicedResults = results.slice(0, 5)
    const transformedTrips = tripDataFilterAndTransform(slicedResults)

    return transformedTrips
  }

  const getGroupTripsByDestination = async (countryId, startIndex, pageSize = 10) => {
    const solrQuery = new SolrQueryBuilder(queryConfig)
    solrQuery
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        bs_groups: true,
        ...(countryId && { its_primary_country_country_nid: countryId }),
      })
      .sort({ bs_special_offer: "desc", its_popularity: "desc" })
      .start(startIndex)
      .rows(pageSize)

    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.response?.docs || null

    return {
      currentPage: Math.ceil(startIndex / pageSize) + 1,
      pages: Math.ceil(response?.response?.numFound / pageSize),
      items: tripDataFilterAndTransform(solrRecords),
    }
  }

  const getTripsByTravelStyle = async (travelStyleId, startIndex, pageSize = 10) => {
    const solrQuery = new SolrQueryBuilder(queryConfig)
    solrQuery
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        bs_groups: true,
        ...(travelStyleId && { itm_travel_style_nid: travelStyleId }),
      })
      .sort({ its_popularity: "desc" })
      .start(startIndex)
      .rows(pageSize)

    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.response?.docs || null

    return {
      currentPage: Math.ceil(startIndex / pageSize) + 1,
      pages: Math.ceil(response?.response?.numFound / pageSize),
      items: tripDataFilterAndTransform(solrRecords),
    }
  }

  const getTripsByNid = async (ids, amount) => {
    const solrQuery = new SolrQueryBuilder(queryConfig)

    // Format IDs properly for Solr query
    // For single ID: its_nid:3699
    // For multiple IDs: its_nid:(3699 OR 3700 OR 3701)
    const idsArray = Array.isArray(ids) ? ids : [ids]
    const idsQuery = idsArray.length === 1 ? idsArray[0] : `(${idsArray.join(' OR ')})`

    solrQuery
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        its_nid: idsQuery,
      })
      .sort({ id: "asc" })
      .rows(amount)
    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.response?.docs || null
    return tripDataFilterAndTransform(solrRecords, true)
  }

  const getTripsBySpecial = async (countryId, startIndex, pageSize = 10) => {
    const solrQuery = new SolrQueryBuilder(queryConfig)
    solrQuery
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        bs_special_offer: true,
        ...(countryId && { its_primary_country_country_nid: countryId }),
      })
      .sort({ bs_special_offer: "desc", its_popularity: "desc" })
      .start(startIndex)
      .rows(pageSize)

    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.response?.docs || null

    return {
      currentPage: Math.ceil(startIndex / pageSize) + 1,
      pages: Math.ceil(response?.response?.numFound / pageSize),
      items: tripDataFilterAndTransform(solrRecords),
    }
  }

  const getTripsByCountry = async (country) => {
    const solrQuery = new SolrQueryBuilder(queryConfig)
    solrQuery
      .customq(["-bs_groups:true OR (*:* -bs_groups:[* TO *])"])
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        ...(country && { its_primary_country_country_nid: country }),
      })
      .sort({ its_popularity: "desc" })

    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.response?.docs || null

    return tripDataFilterAndTransform(solrRecords)
  }

  const getTripsByLocation = async (location) => {
    const solrQuery = new SolrQueryBuilder(queryConfig)
    solrQuery
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        ...(location && { itm_geo_pages_id: location }),
      })
      .sort({ its_popularity: "desc" })

    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.response?.docs || null

    return tripDataFilterAndTransform(solrRecords)
  }

  const getExperienceFacets = async (country) => {
    const solrQuery = new SolrQueryBuilder(queryConfig)
    solrQuery
      // Query that have group field unset (null) or false (not true).
      .customq(["-bs_groups:true OR (*:* -bs_groups:[* TO *])"])
      .q({
        index_id: "trip_index",
        ss_type: "trip",
        ss_gw_combined_country: country,
      })
      .facet({
        on: true,
        field: ["ss_gw_ways_to_travel_keyvalue_str", "ss_gw_travel_style_keyvalue_str"],
        mincount: 1,
      })

    const response = await solrClient.runQuery(solrQuery)
    const solrRecords = response?.facet_counts?.facet_fields || null

    return experienceFacetsTransform(solrRecords)
  }

  return {
    getTripsData,
    getTripsByNid,
    getTripsByTravelStyle,
    getGroupTripsByDestination,
    getTripsBySpecial,
    getTripsByCountryAndDestination,
    getTripsByCountry,
    getTripsByLocation,
    getExperienceFacets,
  }
}

export default useTripsData
